import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Quote } from 'lucide-react';

/**
 * Renders the structured blog content blocks (validated server-side).
 *
 * - `markdown` cells are rendered through react-markdown (remark-gfm), which
 *   never emits raw HTML — authors can't inject scripts or unsafe markup.
 * - Legacy block types (paragraph, heading, image, quote, list) are still
 *   supported so previously published articles keep rendering.
 * - CTA / link cards stay as controlled, theme-matched components.
 */
const BlogContentRenderer = ({ blocks }) => {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
        <p className="text-sm font-semibold text-slate-400">No content yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'markdown':
            return (
              <div key={index} className="my-5">
                <MarkdownContent text={block.text} />
              </div>
            );

          case 'paragraph':
            return (
              <p key={index} className="text-[15px] sm:text-base leading-relaxed text-slate-600 mb-5 last:mb-0">
                {block.text}
              </p>
            );

          case 'heading':
            if (block.level === 'h3') {
              return (
                <h3 key={index} className="text-lg sm:text-xl font-bold text-slate-800 mt-8 mb-4 tracking-tight">
                  {block.text}
                </h3>
              );
            }
            return (
              <h2 key={index} className="text-xl sm:text-2xl font-bold text-slate-800 mt-10 mb-4 tracking-tight">
                {block.text}
              </h2>
            );

          case 'image':
            return (
              <figure key={index} className="my-8">
                <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
                  <img
                    src={block.url}
                    alt={block.alt || 'Blog image'}
                    loading="lazy"
                    className="w-full h-auto object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                {block.caption && (
                  <figcaption className="mt-2.5 text-center text-xs font-medium text-slate-400">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );

          case 'quote':
            return (
              <blockquote key={index} className="relative my-8 pl-6 pr-4 py-5 rounded-r-2xl bg-slate-50 border-l-4 border-primary-600">
                <Quote className="h-5 w-5 text-primary-600 mb-2" />
                <p className="text-base sm:text-lg font-medium text-slate-700 italic leading-relaxed">
                  {block.text}
                </p>
                {block.attribution && (
                  <footer className="mt-2 text-xs font-bold text-primary-600 uppercase tracking-wider">
                    — {block.attribution}
                  </footer>
                )}
              </blockquote>
            );

          case 'cta':
            return (
              <div key={index} className="my-8 text-center">
                {block.url.startsWith('/') ? (
                  <Link
                    to={block.url}
                    className="inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white font-bold py-2.5 px-6 rounded-xl shadow-sm hover:shadow transition-all duration-200"
                  >
                    {block.text}
                  </Link>
                ) : (
                  <a
                    href={block.url}
                    target={block.openInNewTab ? '_blank' : '_self'}
                    rel={block.openInNewTab ? 'noopener noreferrer' : undefined}
                    className="inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white font-bold py-2.5 px-6 rounded-xl shadow-sm hover:shadow transition-all duration-200"
                  >
                    {block.text}
                  </a>
                )}
              </div>
            );

          case 'link_card':
            return (
              <div key={index} className="my-8">
                {block.url.startsWith('/') ? (
                  <Link to={block.url} className="group flex flex-col sm:flex-row items-stretch bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-primary-200 transition-all duration-300 ease-out overflow-hidden">
                    <BlogLinkCardBody block={block} />
                  </Link>
                ) : (
                  <a
                    href={block.url}
                    target={block.openInNewTab ? '_blank' : '_self'}
                    rel={block.openInNewTab ? 'noopener noreferrer' : undefined}
                    className="group flex flex-col sm:flex-row items-stretch bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-primary-200 transition-all duration-300 ease-out overflow-hidden"
                  >
                    <BlogLinkCardBody block={block} />
                  </a>
                )}
              </div>
            );

          case 'divider':
            return <hr key={index} className="my-8 border-slate-200" />;

          case 'list':
            if (block.ordered) {
              return (
                <ol key={index} className="my-5 pl-6 space-y-2.5 list-decimal list-outside text-[15px] sm:text-base leading-relaxed text-slate-600 marker:text-primary-600 marker:font-bold">
                  {block.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ol>
              );
            }
            return (
              <ul key={index} className="my-5 pl-6 space-y-2.5 list-disc list-outside text-[15px] sm:text-base leading-relaxed text-slate-600 marker:text-primary-600">
                {block.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

/** Renders one markdown cell with the Dakhla theme applied to every element. */
const MarkdownContent = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm, remarkMath]}
    rehypePlugins={[rehypeKatex]}
    components={{
      h2: ({ children }) => (
        <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mt-10 mb-4 tracking-tight">{children}</h2>
      ),
      h3: ({ children }) => (
        <h3 className="text-lg sm:text-xl font-bold text-slate-800 mt-8 mb-4 tracking-tight">{children}</h3>
      ),
      h4: ({ children }) => (
        <h4 className="text-base font-bold text-slate-800 mt-6 mb-3 tracking-tight">{children}</h4>
      ),
      p: ({ children }) => (
        <p className="text-[15px] sm:text-base leading-relaxed text-slate-600 mb-5 last:mb-0">{children}</p>
      ),
      a: ({ href, children }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 font-semibold hover:text-primary-700 underline decoration-primary-200 decoration-2 underline-offset-2 transition-colors"
        >
          {children}
        </a>
      ),
      ul: ({ children }) => (
        <ul className="my-5 pl-6 space-y-2.5 list-disc list-outside text-[15px] sm:text-base leading-relaxed text-slate-600 marker:text-primary-600">
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol className="my-5 pl-6 space-y-2.5 list-decimal list-outside text-[15px] sm:text-base leading-relaxed text-slate-600 marker:text-primary-600 marker:font-bold">
          {children}
        </ol>
      ),
      li: ({ children }) => <li>{children}</li>,
      blockquote: ({ children }) => (
        <blockquote className="relative my-8 pl-6 pr-4 py-5 rounded-r-2xl bg-slate-50 border-l-4 border-primary-600">
          <Quote className="h-5 w-5 text-primary-600 mb-2" />
          <div className="text-base sm:text-lg font-medium text-slate-700 italic leading-relaxed">{children}</div>
        </blockquote>
      ),
      img: ({ src, alt }) => (
        <figure className="my-8">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
            <img
              src={src}
              alt={alt || 'Blog image'}
              loading="lazy"
              className="w-full h-auto object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        </figure>
      ),
      hr: () => <hr className="my-8 border-slate-200" />,
      strong: ({ children }) => <strong className="font-bold text-slate-800">{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
      del: ({ children }) => <del className="line-through text-slate-400">{children}</del>,
      code: ({ children }) => (
        <code className="px-1.5 py-0.5 rounded-md bg-slate-100 text-[13px] font-semibold text-[#1E256F] font-mono">
          {children}
        </code>
      ),
      pre: ({ children }) => (
        <pre className="my-5 p-4 rounded-xl bg-slate-900 text-slate-100 text-sm leading-relaxed overflow-x-auto font-mono">
          {children}
        </pre>
      ),
      table: ({ children }) => (
        <div className="my-6 overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm text-slate-600 border-collapse">{children}</table>
        </div>
      ),
      thead: ({ children }) => <thead className="bg-slate-50">{children}</thead>,
      th: ({ children }) => (
        <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
          {children}
        </th>
      ),
      td: ({ children }) => <td className="px-4 py-2.5 border-b border-slate-100 align-top">{children}</td>,
      input: ({ disabled, checked, type }) =>
        type === 'checkbox' ? (
          <input type="checkbox" checked={checked} disabled={disabled} className="mr-2 w-4 h-4 rounded border-slate-300 text-primary-600" />
        ) : null,
    }}
  >
    {text}
  </ReactMarkdown>
);

const BlogLinkCardBody = ({ block }) => (
  <>
    {block.image && (
      <div className="sm:w-44 shrink-0 bg-slate-100">
        <img
          src={block.image}
          alt={block.image_alt || block.title || 'Link card image'}
          loading="lazy"
          className="w-full h-36 sm:h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      </div>
    )}
    <div className="flex-1 p-5 flex flex-col justify-center">
      <h4 className="text-base font-bold text-slate-800 group-hover:text-primary-600 transition-colors">{block.title || 'Learn more'}</h4>
      {block.description && (
        <p className="mt-1.5 text-sm text-slate-500 leading-relaxed line-clamp-2">{block.description}</p>
      )}
      <span className="mt-3 inline-flex items-center text-sm font-semibold text-primary-600 group-hover:text-primary-700 transition-colors">
        {block.button_text || 'View'}
        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 ml-1" />
      </span>
    </div>
  </>
);

export default BlogContentRenderer;
